import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react"
import { useConfig } from "@/config/context"
import {
  completeGoogleSignIn,
  createNostrConnectOffer,
  getNip07Provider,
  GoogleSigner,
  LocalSigner,
  Nip07Signer,
  Nip46Signer,
  ReadOnlySigner,
  SignerError,
  type Nip46Connection,
  type NostrConnectOffer,
  type Signer,
  type SignerMethod,
} from "@/nostr/signers"
import { npubOf, toPubkey } from "@/utils/nip19"
import { readStorage, StorageKeys, writeStorage } from "@/utils/storage"
import { useNostr } from "./NostrContext"
import { useToast } from "./ToastContext"

export interface AccountRef {
  pubkey: string
  method: SignerMethod
}

interface StoredAuth {
  accounts: AccountRef[]
  current?: string
}

/** Saved NIP-46 pairings, keyed by the account they unlock. */
type BunkerStore = Record<string, Nip46Connection>

export interface RemoteSignerAuthOptions {
  /** Called when the signer asks the user to approve the session in a web page. */
  onAuthUrl?: (url: string) => void
}

interface AuthContextValue {
  pubkey?: string
  npub?: string
  method?: SignerMethod
  signer?: Signer
  accounts: AccountRef[]
  /** True when the session can actually sign (extension, remote signer, key). */
  canSign: boolean
  hasExtension: boolean
  /** Google sign-in needs a public OAuth client id from the configuration. */
  canUseGoogle: boolean
  /** The "Create New Account" flow can be turned off per deployment. */
  allowSignup: boolean
  loginWithExtension: () => Promise<void>
  loginWithNsec: (nsec: string) => Promise<void>
  loginWithNpub: (value: string) => void
  /** NIP-46 login with a `bunker://` token or a `name@domain` signer address. */
  loginWithBunker: (input: string, options?: RemoteSignerAuthOptions) => Promise<void>
  /** Creates the `nostrconnect://` URI shown to a signer application. */
  createRemoteSignerOffer: () => NostrConnectOffer
  /** Waits for the signer application to approve the pairing. */
  pairWithRemoteSigner: (offer: NostrConnectOffer, options?: RemoteSignerAuthOptions) => Promise<void>
  /** Verifies a Google ID token and opens the derived identity. */
  loginWithGoogle: (idToken: string, nonce?: string) => Promise<void>
  /** Signs in with a locally generated key (used by the create-account flow). */
  loginWithSecretKey: (secretKey: Uint8Array) => Promise<string>
  switchAccount: (pubkey: string) => Promise<void>
  logout: () => void
  forgetAccount: (pubkey: string) => void
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

function loadStoredAuth(): StoredAuth {
  const stored = readStorage<StoredAuth>(StorageKeys.account, { accounts: [] })
  const accounts = Array.isArray(stored.accounts)
    ? stored.accounts.filter(
        (account): account is AccountRef =>
          Boolean(account) && /^[0-9a-f]{64}$/.test(account.pubkey ?? "") && Boolean(account.method),
      )
    : []
  return { accounts, current: stored.current }
}

/** Only structurally valid pairings survive a reload. */
function loadBunkerStore(): BunkerStore {
  const stored = readStorage<BunkerStore>(StorageKeys.bunker, {})
  const result: BunkerStore = {}
  if (!stored || typeof stored !== "object") return result
  for (const [pubkey, connection] of Object.entries(stored)) {
    if (!/^[0-9a-f]{64}$/.test(pubkey) || !connection) continue
    if (!/^[0-9a-f]{64}$/.test(connection.clientSecretKey ?? "")) continue
    if (!/^[0-9a-f]{64}$/.test(connection.remoteSignerPubkey ?? "")) continue
    if (!Array.isArray(connection.relays) || connection.relays.length === 0) continue
    result[pubkey] = connection
  }
  return result
}

function clientMetadata(name: string) {
  return typeof window === "undefined" ? { name } : { name, url: window.location.origin }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const config = useConfig()
  const { setSigner } = useNostr()
  const { push } = useToast()
  const [stored, setStored] = useState<StoredAuth>(() => loadStoredAuth())
  const [bunkerStore, setBunkerStore] = useState<BunkerStore>(() => loadBunkerStore())
  const [pubkey, setPubkey] = useState<string | undefined>(stored.current)
  const [method, setMethod] = useState<SignerMethod | undefined>(undefined)
  const [signer, setSignerState] = useState<Signer | undefined>(undefined)
  const [hasExtension] = useState(() => Boolean(getNip07Provider()))
  const remoteSigner = useRef<Nip46Signer | undefined>(undefined)

  const canUseGoogle = Boolean(config.auth.googleClientId)
  const allowSignup = config.auth.allowSignup

  const persist = useCallback((next: StoredAuth) => {
    setStored(next)
    writeStorage(StorageKeys.account, next)
  }, [])

  const persistBunker = useCallback((accountPubkey: string, connection: Nip46Connection | undefined) => {
    setBunkerStore((current) => {
      const next = { ...current }
      if (connection) next[accountPubkey] = connection
      else delete next[accountPubkey]
      writeStorage(StorageKeys.bunker, next)
      return next
    })
  }, [])

  const closeRemoteSigner = useCallback(() => {
    const active = remoteSigner.current
    remoteSigner.current = undefined
    void active?.close()
  }, [])

  const applySession = useCallback(
    (nextPubkey: string | undefined, nextSigner: Signer | undefined, nextMethod?: SignerMethod) => {
      if (remoteSigner.current && nextSigner !== remoteSigner.current) closeRemoteSigner()
      if (nextSigner instanceof Nip46Signer) remoteSigner.current = nextSigner
      setPubkey(nextPubkey)
      setSignerState(nextSigner)
      setMethod(nextMethod)
      setSigner(nextSigner)
    },
    [closeRemoteSigner, setSigner],
  )

  // Restore the last session once: extensions are asked silently, NIP-46
  // pairings are re-opened, and everything else is downgraded to read-only
  // because secret keys are never persisted.
  useEffect(() => {
    let cancelled = false
    const restore = async () => {
      if (!stored.current) return
      const account = stored.accounts.find((item) => item.pubkey === stored.current)
      if (!account) return

      if (account.method === "nip07") {
        const provider = getNip07Provider()
        if (provider) {
          try {
            const current = await provider.getPublicKey()
            if (cancelled) return
            if (current === account.pubkey) {
              applySession(account.pubkey, new Nip07Signer(provider), "nip07")
              return
            }
          } catch {
            // the user declined the silent permission request
          }
        }
      }

      if (account.method === "nip46") {
        const connection = bunkerStore[account.pubkey]
        if (connection) {
          try {
            const nextSigner = await Nip46Signer.fromConnection(connection)
            if (cancelled) {
              void nextSigner.close()
              return
            }
            if (nextSigner.publicKey === account.pubkey) {
              applySession(account.pubkey, nextSigner, "nip46")
              return
            }
            void nextSigner.close()
          } catch {
            // the signer is offline; fall back to a read-only session
          }
        }
      }

      // Google sessions cannot be restored silently: the key is derived from a
      // fresh identity token, so the user signs in again to unlock signing.
      if (!cancelled) applySession(account.pubkey, new ReadOnlySigner(account.pubkey), "npub")
    }
    void restore()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const rememberAccount = useCallback(
    (account: AccountRef) => {
      const accounts = [
        account,
        ...stored.accounts.filter((item) => item.pubkey !== account.pubkey),
      ].slice(0, 10)
      persist({ accounts, current: account.pubkey })
    },
    [persist, stored.accounts],
  )

  const loginWithExtension = useCallback(async () => {
    const provider = getNip07Provider()
    if (!provider) throw new SignerError("no NIP-07 extension found in this browser")
    const nextSigner = new Nip07Signer(provider)
    const nextPubkey = await nextSigner.getPublicKey()
    rememberAccount({ pubkey: nextPubkey, method: "nip07" })
    applySession(nextPubkey, nextSigner, "nip07")
    push("success", "Signed in with your browser extension")
  }, [applySession, push, rememberAccount])

  const loginWithNsec = useCallback(
    async (nsec: string) => {
      const nextSigner = LocalSigner.fromNsec(nsec)
      const nextPubkey = await nextSigner.getPublicKey()
      rememberAccount({ pubkey: nextPubkey, method: "nsec" })
      applySession(nextPubkey, nextSigner, "nsec")
      push("success", "Signed in — the key stays in memory for this session only")
    },
    [applySession, push, rememberAccount],
  )

  const loginWithSecretKey = useCallback(
    async (secretKey: Uint8Array) => {
      const nextSigner = new LocalSigner(secretKey)
      const nextPubkey = await nextSigner.getPublicKey()
      rememberAccount({ pubkey: nextPubkey, method: "nsec" })
      applySession(nextPubkey, nextSigner, "nsec")
      return nextPubkey
    },
    [applySession, rememberAccount],
  )

  const loginWithNpub = useCallback(
    (value: string) => {
      const nextPubkey = toPubkey(value)
      if (!nextPubkey) {
        push("error", "That is not a valid npub, nprofile or hex public key")
        return
      }
      rememberAccount({ pubkey: nextPubkey, method: "npub" })
      applySession(nextPubkey, new ReadOnlySigner(nextPubkey), "npub")
      push("info", "Browsing in read-only mode")
    },
    [applySession, push, rememberAccount],
  )

  const loginWithBunker = useCallback(
    async (input: string, options: RemoteSignerAuthOptions = {}) => {
      const nextSigner = await Nip46Signer.fromBunkerUri(input, {
        timeoutMs: 90_000,
        onAuthUrl: options.onAuthUrl,
        clientMetadata: clientMetadata(config.branding.name),
      })
      const nextPubkey = await nextSigner.getPublicKey()
      rememberAccount({ pubkey: nextPubkey, method: "nip46" })
      persistBunker(nextPubkey, nextSigner.getConnection())
      applySession(nextPubkey, nextSigner, "nip46")
      push("success", "Connected to your remote signer")
    },
    [applySession, config.branding.name, persistBunker, push, rememberAccount],
  )

  const createRemoteSignerOffer = useCallback(
    () =>
      createNostrConnectOffer({
        relays: config.auth.nip46Relays,
        appName: config.branding.name,
        appUrl: typeof window === "undefined" ? undefined : window.location.origin,
        permissions: config.auth.nip46Permissions,
      }),
    [config.auth.nip46Permissions, config.auth.nip46Relays, config.branding.name],
  )

  const pairWithRemoteSigner = useCallback(
    async (offer: NostrConnectOffer, options: RemoteSignerAuthOptions = {}) => {
      const nextSigner = await Nip46Signer.pair(offer, {
        timeoutMs: 90_000,
        pairTimeoutMs: 180_000,
        onAuthUrl: options.onAuthUrl,
        clientMetadata: clientMetadata(config.branding.name),
      })
      const nextPubkey = await nextSigner.getPublicKey()
      rememberAccount({ pubkey: nextPubkey, method: "nip46" })
      persistBunker(nextPubkey, nextSigner.getConnection())
      applySession(nextPubkey, nextSigner, "nip46")
      push("success", "Paired with your signer app")
    },
    [applySession, config.branding.name, persistBunker, push, rememberAccount],
  )

  const loginWithGoogle = useCallback(
    async (idToken: string, nonce?: string) => {
      if (!config.auth.googleClientId) {
        throw new SignerError("Google sign-in is not configured for this deployment")
      }
      const { identity, secretKey } = await completeGoogleSignIn(idToken, {
        clientId: config.auth.googleClientId,
        nonce,
      })
      const nextSigner = new GoogleSigner(secretKey, identity)
      const nextPubkey = await nextSigner.getPublicKey()
      rememberAccount({ pubkey: nextPubkey, method: "google" })
      applySession(nextPubkey, nextSigner, "google")
      push("success", identity.email ? `Signed in as ${identity.email}` : "Signed in with Google")
    },
    [applySession, config.auth.googleClientId, push, rememberAccount],
  )

  const switchAccount = useCallback(
    async (targetPubkey: string) => {
      const account = stored.accounts.find((item) => item.pubkey === targetPubkey)
      if (!account) return

      if (account.method === "nip07") {
        const provider = getNip07Provider()
        if (provider) {
          try {
            const current = await provider.getPublicKey()
            if (current === targetPubkey) {
              persist({ ...stored, current: targetPubkey })
              applySession(targetPubkey, new Nip07Signer(provider), "nip07")
              return
            }
            push("error", "Your extension is unlocked with a different key")
          } catch (error) {
            push("error", error instanceof Error ? error.message : "The extension refused the request")
          }
        }
      }

      if (account.method === "nip46") {
        const connection = bunkerStore[targetPubkey]
        if (connection) {
          try {
            const nextSigner = await Nip46Signer.fromConnection(connection)
            if (nextSigner.publicKey === targetPubkey) {
              persist({ ...stored, current: targetPubkey })
              applySession(targetPubkey, nextSigner, "nip46")
              return
            }
            void nextSigner.close()
          } catch (error) {
            push("error", error instanceof Error ? error.message : "The remote signer is not reachable")
          }
        }
      }

      persist({ ...stored, current: targetPubkey })
      applySession(targetPubkey, new ReadOnlySigner(targetPubkey), "npub")
    },
    [applySession, bunkerStore, persist, push, stored],
  )

  const logout = useCallback(() => {
    persist({ accounts: stored.accounts, current: undefined })
    applySession(undefined, undefined, undefined)
  }, [applySession, persist, stored.accounts])

  const forgetAccount = useCallback(
    (targetPubkey: string) => {
      const accounts = stored.accounts.filter((item) => item.pubkey !== targetPubkey)
      const current = stored.current === targetPubkey ? undefined : stored.current
      persist({ accounts, current })
      persistBunker(targetPubkey, undefined)
      if (stored.current === targetPubkey) applySession(undefined, undefined, undefined)
    },
    [applySession, persist, persistBunker, stored],
  )

  const value = useMemo<AuthContextValue>(
    () => ({
      pubkey,
      npub: pubkey ? npubOf(pubkey) : undefined,
      method,
      signer,
      accounts: stored.accounts,
      canSign: Boolean(signer) && method !== "npub",
      hasExtension,
      canUseGoogle,
      allowSignup,
      loginWithExtension,
      loginWithNsec,
      loginWithNpub,
      loginWithBunker,
      createRemoteSignerOffer,
      pairWithRemoteSigner,
      loginWithGoogle,
      loginWithSecretKey,
      switchAccount,
      logout,
      forgetAccount,
    }),
    [
      pubkey,
      method,
      signer,
      stored.accounts,
      hasExtension,
      canUseGoogle,
      allowSignup,
      loginWithExtension,
      loginWithNsec,
      loginWithNpub,
      loginWithBunker,
      createRemoteSignerOffer,
      pairWithRemoteSigner,
      loginWithGoogle,
      loginWithSecretKey,
      switchAccount,
      logout,
      forgetAccount,
    ],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext)
  if (!context) throw new Error("useAuth must be used inside an AuthProvider")
  return context
}
